import {FormControl, FormGroup} from '@angular/forms';
import {ConsumptionToMonitor} from '../consumption-agent-monitoring/consumption-agent-monitoring.model';
import {Translation} from '@brunata-angular/util';

export const AGENT_SCHEDULES = [
    'DISABLED',
    'DAILY_8AM',
    'WEEKLY_EVERY_MONDAY_8AM',
    'BIMONTHLY_EVERY_SECOND_MONDAY_8AM',
    'MONTHLY_FIRST_8AM',
] as const;

export const CONSUMPTION_AGENT_TYPES = [
    'FIXED_VALUE',
    'INCREASING_DECREASING',
    'GROUP_COMPARISON',
] as const;
export type ConsumptionAgentType = (typeof CONSUMPTION_AGENT_TYPES)[number];

const FIXED_OPERATORS = ['HIGHER', 'LOWER', 'EQUAL_TO'] as const;
const INCREASING_DECREASING_OPERATORS = ['INCREASING', 'DECREASING'] as const;
const GROUP_COMPARISON_OPERATORS = ['HIGHER_THAN_GROUP2', 'LOWER_THAN_GROUP2'] as const;

export const CONSUMPTION_AGENT_OPERATORS = [
    ...FIXED_OPERATORS,
    ...INCREASING_DECREASING_OPERATORS,
    ...GROUP_COMPARISON_OPERATORS,
] as const;

export type ConsumptionAgentOperator = (typeof CONSUMPTION_AGENT_OPERATORS)[number];

export const CONSUMPTION_AGENT_COMPARISON_PERIODS = [
    'PREVIOUS_PERIOD_SAME_LENGTH',
    'SAME_PERIOD_PREVIOUS_YEAR',
] as const;

export type IncreaseDecreaseAgentComparisonPeriod =
    (typeof CONSUMPTION_AGENT_COMPARISON_PERIODS)[number];

export type ConsumptionAgentSchedule = (typeof AGENT_SCHEDULES)[number];

type InputType = 'radio' | 'number';

type BaseInput = {
    labelKey: string;
    formControlName: keyof ConsumptionAgentInvestigationSettings;
    type: InputType;
    class?: string;
    descriptionKey?: string;
};

type RadioButton<T> = BaseInput & {
    type: 'radio';
    key: T;
};

type NumberInput = BaseInput & {
    type: 'number';
    descriptor?: Translation;
    formControlName: keyof ConsumptionAgentInvestigationSettings;
};

export type ConsumptionAgentTypeInvestigation = {
    key: ConsumptionAgentType;
    labelKey: string;
    columns: Column<ConsumptionAgentOperator>[];
    subInputGroup?: InvestigationSubInputGroup;
    requiredconsumptionToMonitor?: ConsumptionToMonitor;
    class?: string;
    headerKey?: string;
    descriptionKey?: string;
};

export type ConsumptionAgentInvestigationSettings = {
    agentType: ConsumptionAgentType;
    operator: ConsumptionAgentOperator;
    comparisonPeriod: IncreaseDecreaseAgentComparisonPeriod;
    fixedValue: number | null;
    percentage: number | null;
    minUnitCount: number | null;
};

export type ConsumptionAgentInvestigationFormGroup = FormGroup<{
    [K in keyof ConsumptionAgentInvestigationSettings]: FormControl<
        ConsumptionAgentInvestigationSettings[K]
    >;
}>;

type Input<T> = RadioButton<T> | NumberInput;

type Column<T> = {
    class: string;
    inputs: Input<T>[];
};

type InvestigationSubInputGroup = {
    inputs: RadioButton<IncreaseDecreaseAgentComparisonPeriod>[];
    class?: string;
    headerKey?: string;
};

export const CONSUMPTION_AGENT_TYPE_INVESTIGATIONS: ConsumptionAgentTypeInvestigation[] = [
    {
        key: 'FIXED_VALUE',
        labelKey: 'monitoring_consumption_is_higher/lower/equal_fixed_value',
        class: 'ms-5 row',
        descriptionKey: 'consumption-agents_create_investigation_fixed_value_description',
        columns: [
            {
                class: 'col-6',
                inputs: [
                    {
                        key: 'HIGHER',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_higher',
                        formControlName: 'operator',
                    },
                    {
                        key: 'LOWER',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_lower',
                        formControlName: 'operator',
                    },
                    {
                        key: 'EQUAL_TO',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_equalTo',
                        formControlName: 'operator',
                    },
                ],
            },
            {
                class: 'col-6',
                inputs: [
                    {
                        labelKey: 'monitoring_value',
                        type: 'number',
                        formControlName: 'fixedValue',
                        descriptionKey: 'consumption-agents_create_fixed_value_description',
                    },
                ],
            },
        ],
    },
    {
        key: 'INCREASING_DECREASING',
        labelKey: 'monitoring_consumption_over_time',
        class: 'ms-5 row',
        descriptionKey: 'consumption-agents_create_investigation_increase_decrease_description',
        columns: [
            {
                class: 'col-6',
                inputs: [
                    {
                        key: 'INCREASING',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_Increasing',
                        formControlName: 'operator',
                    },
                    {
                        key: 'DECREASING',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_decreasing',
                        formControlName: 'operator',
                    },
                ],
            },
            {
                class: 'col-6',
                inputs: [
                    {
                        labelKey: 'monitoring_Percentual-change',
                        type: 'number',
                        formControlName: 'percentage',
                        descriptor: {
                            key: 'mappers_measurementUnit.84',
                            module: 'common',
                        },
                    },
                    {
                        labelKey: 'monitoring_Trivial-amount',
                        type: 'number',
                        formControlName: 'minUnitCount',
                        descriptionKey: 'consumption-agents_create_trivial_amount_description',
                    },
                ],
            },
        ],
        subInputGroup: {
            headerKey: 'monitoring_COMPARE_WITH',
            class: 'ms-3',
            inputs: [
                {
                    key: 'PREVIOUS_PERIOD_SAME_LENGTH',
                    type: 'radio',
                    class: 'form-check',
                    labelKey: 'monitoring_Previous-period-of-same-length',
                    formControlName: 'comparisonPeriod',
                },
                {
                    key: 'SAME_PERIOD_PREVIOUS_YEAR',
                    type: 'radio',
                    class: 'form-check',
                    labelKey: 'monitoring_Same-period-previous-year',
                    formControlName: 'comparisonPeriod',
                },
            ],
        },
    },
    {
        key: 'GROUP_COMPARISON',
        requiredconsumptionToMonitor: 'DIFFERENCECONSUMPTION',
        labelKey: 'monitoring_consumption_compared',
        class: 'ms-5 row',
        columns: [
            {
                class: 'col-6',
                inputs: [
                    {
                        labelKey: 'monitoring_The-consumption-in-meter-group-1-is-more-than',
                        type: 'number',
                        formControlName: 'percentage',
                        descriptor: {
                            key: 'mappers_measurementUnit.84',
                            module: 'common',
                        },
                    },
                ],
            },
            {
                class: 'col-12',
                inputs: [
                    {
                        key: 'HIGHER_THAN_GROUP2',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_higher-than-the-consumption-in-meter-group-2',
                        formControlName: 'operator',
                    },
                    {
                        key: 'LOWER_THAN_GROUP2',
                        type: 'radio',
                        class: 'form-check',
                        labelKey: 'monitoring_lower-than-the-consumption-in-meter-group-2',
                        formControlName: 'operator',
                    },
                ],
            },
        ],
    },
];

export const AGENT_TYPE_OPERATOR_MAP: Record<ConsumptionAgentType, ConsumptionAgentOperator[]> = {
    FIXED_VALUE: [...FIXED_OPERATORS],
    INCREASING_DECREASING: [...INCREASING_DECREASING_OPERATORS],
    GROUP_COMPARISON: [...GROUP_COMPARISON_OPERATORS],
};

export const AGENT_TYPE_FORM_GROUP_KEY_MAP: Record<
    ConsumptionAgentType,
    Partial<keyof ConsumptionAgentInvestigationSettings>[]
> = {
    FIXED_VALUE: ['fixedValue'],
    INCREASING_DECREASING: ['comparisonPeriod', 'percentage', 'minUnitCount'],
    GROUP_COMPARISON: ['percentage'],
};
